"use client";

import { trpc } from "@/lib/trpc-client";

/**
 * What is unread in each of Inbox's views: the dots on its Messages and
 * Updates switch, and the one dot the tab bar and rail put on Inbox for both.
 *
 * Read from the pages of each list already loaded, which is where anything
 * new sorts, as the iOS app does. The queries are the ones the views run, so
 * the shell and the open view share one cache: Messages' own polling keeps
 * the dot current while it is on screen, and marking an update read clears
 * it. Nothing here polls on its own.
 *
 * `enabled` is for the shell, which also draws for someone not signed in.
 */
export function useInboxUnread({ enabled = true }: { enabled?: boolean } = {}) {
  const conversations = trpc.creatorMessaging.creator.list.useInfiniteQuery(
    {},
    { enabled, getNextPageParam: (page) => page.nextCursor, retry: false },
  );
  // The whole feed, not the one update a link may have opened, so the dot
  // still says whether anything else is waiting.
  const updates = trpc.creatorMessaging.updates.creatorList.useInfiniteQuery(
    { id: undefined },
    { enabled, getNextPageParam: (page) => page.nextCursor },
  );
  return {
    messages:
      conversations.data?.pages
        .flatMap((page) => page.conversations)
        .filter((row) => row.unread).length ?? 0,
    updates:
      updates.data?.pages
        .flatMap((page) => page.updates)
        .filter((update) => !update.read).length ?? 0,
  };
}
